import { useCallback, useEffect, useRef, useState } from "react";
import { useAgentBridge } from "../../app/AgentBridge";
import { ImportView } from "../../shared/ImportView";
import { fetchReferenzMd, saveReferenzInput, type InqaReferenzInput } from "./api";
import { buildInqaPrompt } from "./prompts";
import { applyInqaPrefill } from "./prefill";
import { Step1Form } from "./Step1Form";
import { Step2Edit } from "./Step2Edit";
import { Step3Download } from "./Step3Download";
import { StepIndicator } from "./StepIndicator";
import {
  buildInqaSlug,
  clearPersistedInqaState,
  loadPersistedInqaState,
  persistInqaState,
  type InqaForm,
  type InqaState,
} from "./state";

export function InqaWizard() {
  const { send, subscribe } = useAgentBridge();
  const [state, setState] = useState<InqaState>(loadPersistedInqaState);
  const pendingGenerateRef = useRef(false);
  const [generating, setGenerating] = useState(false);
  const [referenzContent, setReferenzContent] = useState<string>("");
  const [hint, setHint] = useState<string | null>(null);
  const [prefillBanner, setPrefillBanner] = useState<string | null>(null);

  useEffect(() => {
    persistInqaState(state);
  }, [state]);

  const setStep = useCallback(
    (step: InqaState["step"]) =>
      setState((prev) => {
        const maxReachedStep = (step > prev.maxReachedStep ? step : prev.maxReachedStep) as InqaState["maxReachedStep"];
        return { ...prev, phase: "wizard", step, maxReachedStep };
      }),
    [],
  );

  const skipImport = useCallback(() => {
    setState((prev) => ({ ...prev, phase: "wizard" }));
  }, []);

  const jumpToImport = useCallback(() => {
    setState((prev) => ({ ...prev, phase: "import" }));
  }, []);

  const patchForm = useCallback((patch: Partial<InqaForm>) => {
    setState((prev) => ({ ...prev, form: { ...prev.form, ...patch } }));
  }, []);

  const submitGenerate = useCallback(async () => {
    const { form } = state;
    if (!form.projekttitel.trim()) {
      setHint("Bitte gib einen Projekttitel ein.");
      return;
    }
    if (form.gestaltungsfelder.length === 0) {
      setHint("Bitte mindestens ein Gestaltungsfeld auswählen.");
      return;
    }
    const slug = buildInqaSlug(form.projekttitel);
    const payload: InqaReferenzInput = {
      meta: {
        referenz_type: "inqa",
        coach_name: form.coachName,
        coach_unternehmen: form.coachUnternehmen,
      },
      projekt: {
        titel: form.projekttitel,
        website_url: form.websiteUrl.trim(),
        unternehmen_name: "",
        kontaktdaten_unternehmen: form.kontaktdaten,
        branche: form.branche,
        mitarbeitende: form.mitarbeitende,
        personentage: form.personentage ? Number(form.personentage) : null,
        stunden_pro_tag: form.stundenProTag ? Number(form.stundenProTag) : 8,
        zeitraum_start: form.zeitraumStart,
        zeitraum_end: form.zeitraumEnd,
      },
      gestaltungsfelder: form.gestaltungsfelder,
      ausgangssituation_stichpunkte: form.ausgangssituation,
      zielsetzung_stichpunkte: form.zielsetzung,
      kerninhalte_stichpunkte: form.kerninhalte,
      methoden_stichpunkte: form.methoden,
      ergebnisse_stichpunkte: form.ergebnisse,
      beteiligte_rollen: form.rollen,
    };

    try {
      await saveReferenzInput(slug, payload);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setHint("Speichern fehlgeschlagen: " + message);
      return;
    }

    setState((prev) => ({ ...prev, currentSlug: slug }));
    pendingGenerateRef.current = true;
    setGenerating(true);
    setHint(null);
    const prompt = buildInqaPrompt(form, slug);
    if (!send({ type: "message", content: prompt, mode: "inqa" })) {
      pendingGenerateRef.current = false;
      setGenerating(false);
      setHint("Nicht mit dem Agenten verbunden.");
    }
  }, [state, send]);

  const newReferenz = useCallback(() => {
    clearPersistedInqaState();
    setState(loadPersistedInqaState());
    setReferenzContent("");
    setHint(null);
    pendingGenerateRef.current = false;
    setGenerating(false);
  }, []);

  const loadReferenz = useCallback(async (slug: string) => {
    const content = await fetchReferenzMd(slug);
    if (content == null) return false;
    setReferenzContent(content);
    return true;
  }, []);

  // ── React to agent server events ────────────────────────────────────────
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type === "import_prefill_result" && msg.mode === "inqa") {
        setState((prev) => applyInqaPrefill(prev, msg.data ?? {}));
        setPrefillBanner(
          "Felder wurden aus den importierten Daten vorausgefüllt. Bitte prüfen und bei Bedarf ergänzen.",
        );
        return;
      }
      if (msg.type === "done") {
        if (!pendingGenerateRef.current) return;
        const slug = state.currentSlug;
        pendingGenerateRef.current = false;
        setGenerating(false);
        if (!slug) return;
        void (async () => {
          const ok = await loadReferenz(slug);
          if (ok) {
            setState((prev) => ({
              ...prev,
              step: 2,
              maxReachedStep: prev.maxReachedStep < 2 ? 2 : prev.maxReachedStep,
            }));
          } else {
            setHint("referenz.md nicht gefunden. Agent hat die Datei möglicherweise nicht geschrieben.");
          }
        })();
        return;
      }
      if (msg.type === "error") {
        if (pendingGenerateRef.current) {
          pendingGenerateRef.current = false;
          setGenerating(false);
        }
        return;
      }
      if (msg.type === "file_changed" && msg.fileType === "inqa_referenz") {
        const slug = state.currentSlug;
        if (!slug) return;
        void (async () => {
          const content = await fetchReferenzMd(slug);
          if (content == null) return;
          setReferenzContent(content);
          // Surface the result on Step 2 even if the user is still on Step 1.
          setState((prev) => {
            const maxReachedStep = (prev.maxReachedStep < 2 ? 2 : prev.maxReachedStep) as InqaState["maxReachedStep"];
            return { ...prev, step: prev.step === 1 ? 2 : prev.step, maxReachedStep };
          });
        })();
      }
    });
  }, [subscribe, state.currentSlug, loadReferenz]);

  // Re-fetch the referenz when navigating back to Step 2/3 after a reload.
  useEffect(() => {
    if (state.step < 2) return;
    if (referenzContent) return;
    if (!state.currentSlug) return;
    void loadReferenz(state.currentSlug);
  }, [state.step, state.currentSlug, referenzContent, loadReferenz]);

  return (
    <div className="inqa-wizard">
      <StepIndicator
        phase={state.phase}
        step={state.step}
        maxReachedStep={state.maxReachedStep}
        onJumpToImport={jumpToImport}
        onJumpToStep={setStep}
      />

      {prefillBanner && (
        <div className="import-prefill-banner" role="status">
          {prefillBanner}{" "}
          <button type="button" className="link-button" onClick={() => setPrefillBanner(null)}>
            Schließen
          </button>
        </div>
      )}

      {hint && (
        <div className="info-box" role="status">
          {hint}{" "}
          <button type="button" className="link-button" onClick={() => setHint(null)}>
            Schließen
          </button>
        </div>
      )}

      {state.phase === "import" && <ImportView mode="inqa" onSkip={skipImport} />}
      {state.phase === "wizard" && state.step === 1 && (
        <Step1Form
          form={state.form}
          loading={generating}
          onChange={patchForm}
          onSubmit={() => void submitGenerate()}
        />
      )}
      {state.phase === "wizard" && state.step === 2 && (
        <Step2Edit
          referenzContent={referenzContent}
          slug={state.currentSlug}
          onNext={async (flush) => {
            await flush();
            setStep(3);
          }}
        />
      )}
      {state.phase === "wizard" && state.step === 3 && (
        <Step3Download
          slug={state.currentSlug}
          referenzContent={referenzContent}
          onNewReferenz={newReferenz}
        />
      )}
    </div>
  );
}
